import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  Chart,
  ChartDataset,
  ChartOptions,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  RadarController,
  PolarAreaController,
  ArcElement
} from 'chart.js';

import { CONFIG } from '@cpd-app/configuration';

Chart.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  RadarController,
  PolarAreaController,
  ArcElement
);

@Component({
  selector: 'cpd-interests-chart-radar',
  templateUrl: './interests-chart-radar.component.html',
  styleUrls: ['./interests-chart-radar.component.scss']
})
export class InterestsChartRadarComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @ViewChild('interestsChart', { static: true })
  public interestsChartCanvas: ElementRef;

  @Input() public data: number[];
  @Input() public datasets: ChartDataset[];
  @Input() public options: ChartOptions;
  @Input() public isSmall = false; // screen size
  @Input() public beforeDraw = false;
  @Input() public afterDraw = false;
  @Input() public backgroundColor = '#2C4971';
  /**
   * 'navy' is the chart as it has always drawn, on its own navy panel.
   * 'light' draws on a white card: pale grid, labels in the page's ink (the
   * point colours stay, so the dots still match the trait colours), and a
   * light blue fill. For the interests results, where the navy panel looked
   * like a different component from the white cards around it.
   */
  @Input() public theme: 'navy' | 'light' = 'navy';
  @Output() public chartClick = new EventEmitter<number>();

  private theChart: Chart;

  /*
   * ANALYTICS.12. This method used to run `Chart.defaults.font.size = 0`.
   *
   * Chart.defaults is GLOBAL to the Chart.js module and shared by every chart
   * in the application, and nothing ever put it back. So once a learner had
   * opened any page carrying this radar, every chart tooltip everywhere else
   * drew its text at zero pixels: the analytics dashboard, the match score
   * chart, and this component's own siblings. On Traffic Overview that rendered
   * as an empty white box beside the hovered point, which is exactly what was
   * reported. Axis labels survived only because they set their own size.
   *
   * The zero is KEPT, because this chart really does want it, but it is applied
   * to this chart's own options instead of to every chart in the process. See
   * initializeChart().
   */
  public ngOnInit(): void {
    // Intentionally empty; see the note above.
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      (this.theChart &&
        changes['isSmall'] &&
        !changes['isSmall'].firstChange) ||
      (changes['datasets'] && !changes['datasets'].firstChange)
    ) {
      this.theChart.destroy();

      this.initializeChart();
    }
  }

  public ngAfterViewInit(): void {
    this.initializeChart();
  }

  private initializeChart(): void {
    const chartContext = (<HTMLCanvasElement>(
      this.interestsChartCanvas.nativeElement
    )).getContext('2d');
    const chartOptions: ChartOptions = {
      ...(this.options || this.configureRadialChart(this.isSmall)),
      /*
       * Per-chart font default, replacing the global write that used to live in
       * ngOnInit. Chart.js resolves font options through a scope chain that
       * falls back to the chart's own options, so anything here that does not
       * set its own size still draws at zero, which is what this radar has
       * always done. Callers that pass `options` get the same treatment, which
       * matters: the Goals Report's radar sets a tick stepSize without
       * `display: false` and has only ever been legible because of the zero.
       */
      font: { size: 0 }
    } as ChartOptions;

    const data = this.data || this.datasets[0]?.data;

    const chartLabels = CONFIG.INTERESTS_CHART.LABELS.map((label, index) =>
      this.pointLabel(label, data?.[index] as number)
    );

    if (this.data && !this.datasets?.length) {
      this.datasets = [
        {
          data: this.data,
          pointBackgroundColor: CONFIG.INTERESTS_CHART.COLORS,
          pointRadius: 8,
          pointHoverRadius: 10,
          pointStyle: 'circle',
          backgroundColor:
            this.theme === 'light'
              ? 'rgba(53, 94, 147, 0.16)'
              : 'rgba(72, 110, 151, 0.7)',
          borderColor: this.theme === 'light' ? '#355E93' : '#FFF'
        }
      ];
    }

    this.theChart = new Chart(chartContext, {
      type: 'radar',
      data: {
        labels: chartLabels,
        datasets: this.datasets
      },
      options: chartOptions,
      plugins: [
        {
          id: 'custom_canvas_background_color',
          beforeDraw: this.beforeDraw ? this.charBeforeDraw.bind(this) : null,
          afterDraw: this.afterDraw ? this.chartAfterDraw : null
        }
      ]
    });
  }

  /*
   * A point's label: "REALISTIC (71%)" on one line, or on a phone, with this
   * component's own options, the name and the percent on two lines so the
   * labels leave room for the chart. Callers passing `options` style their
   * own labels and keep the one-line form.
   */
  private pointLabel(label: string, value?: number): string | string[] {
    if (value === undefined || value === null) {
      return label;
    }

    return this.isSmall && !this.options
      ? [label, `${value}%`]
      : `${label} (${value}%)`;
  }

  private configureRadialChart(isSmallDevice: boolean): ChartOptions {
    return {
      aspectRatio: 1,
      maintainAspectRatio: false,
      responsive: true,
      animation: undefined,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'white',
          titleFont: {
            size: 12,
            family: 'Arial'
          },
          bodyFont: {
            size: 12,
            family: 'Arial'
          },
          titleColor: '#000',
          bodyColor: '#000',
          cornerRadius: 5,
          caretPadding: 10,
          callbacks: {
            label: () => 'Click to learn more'
          }
        }
      },
      scales: {
        r: {
          beginAtZero: true,
          min: 0,
          max: 100,
          ticks: {
            stepSize: 33,
            display: false
          },
          angleLines: {
            display: true,
            lineWidth: 1,
            color: this.theme === 'light' ? '#DCE3EC' : '#1E3757'
          },
          grid: {
            color: this.theme === 'light' ? '#E3E9F1' : '#365E8F'
          },
          /*
           * Phones used to hide these, leaving six colored dots whose only
           * key was a legend below the chart: color alone said which trait
           * was which. They now show on phones too, in white (12:1 on the
           * navy page, where the trait colors reach only 3.1:1 to 5.1:1),
           * split over two lines to fit. See pointLabel().
           */
          pointLabels: {
            display: true,
            font: {
              size: isSmallDevice ? 11 : 14,
              // On the light theme the labels sit in the page's own type.
              ...(this.theme === 'light' && typeof document !== 'undefined'
                ? {
                    family: getComputedStyle(document.body).fontFamily,
                    weight: '600'
                  }
                : {})
            },
            color: (ctx) => {
              if (this.theme === 'light') {
                return '#13293F';
              }
              return isSmallDevice
                ? '#FFFFFF'
                : CONFIG.INTERESTS_CHART.COLORS[ctx.index];
            }
          }
        }
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: isSmallDevice ? 5 : 20,
          bottom: isSmallDevice ? 5 : 20
        }
      },
      elements: {
        line: {
          borderWidth: 1,
          tension: 0.35,
          borderCapStyle: 'round',
          borderDash: [2, 4]
        }
      },
      onClick: (event, activeElements) => {
        if (activeElements?.length > 0) {
          this.chartClick.emit(activeElements[0]['index']);
        }
      }
    };
  }

  private charBeforeDraw(chart, _easing): void {
    if (chart) {
      const { ctx, scales } = chart;
      const { xCenter, yCenter, drawingArea: radius } = scales.r;

      // this is to fill the background of the hexagon
      ctx.beginPath();
      ctx.moveTo(
        xCenter + radius * Math.cos(0),
        yCenter + radius * Math.sin(0)
      );

      for (let i = 0; i < 7; i++) {
        ctx.lineTo(
          xCenter + radius * Math.cos((i * 2 * Math.PI) / 6 + Math.PI / 6),
          yCenter + radius * Math.sin((i * 2 * Math.PI) / 6 + Math.PI / 6)
        );
      }

      ctx.fillStyle = this.backgroundColor;
      ctx.fill();
      ctx.closePath();
      ctx.restore();
    }
  }

  //  this stuff needs to happen after the chart has been drawn to put this on top of it
  private chartAfterDraw(chart, easing): void {
    const { ctx, scales } = chart;
    const { xCenter, yCenter, drawingArea: radius } = scales.r;

    ctx.lineWidth = 2;
    ctx.strokeStyle = 'white';

    for (let i = 0; i < 6; i++) {
      const x =
        xCenter + radius * Math.cos((i * 2 * Math.PI) / 6 + 1.5 * Math.PI);
      const y =
        yCenter + radius * Math.sin((i * 2 * Math.PI) / 6 + 1.5 * Math.PI);

      // draw the circles
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, 2 * Math.PI);
      ctx.fillStyle = CONFIG.INTERESTS_CHART.COLORS[i];
      ctx.fill();
    }

    ctx.restore();
  }
}
